import ExpandableRow from '../ExpandableRow';
import { Fragment, useMemo } from 'react';

import type { FunctionComponent, Props } from './types';

const DataGridRow: FunctionComponent<Props> = ({
    allRows,
    indent = 0,
    isLast = [],
    onToggleOpen,
    openItems,
    parentId,
    row,
    rowIndex,
    style
}) => {
    const isOpen = openItems ? openItems[row.id] : false;
    const hasChildren = row?.childIds ? row?.childIds?.length > 0 : false;

    const handleClick = () => {
        onToggleOpen(row.id);
    };

    const siblings = useMemo(
        () => (allRows || []).filter(r => r.parentId === parentId),
        [allRows, parentId]
    );

    const children = useMemo(() => {
        if (!hasChildren || !allRows) {
            return [];
        }

        return allRows.filter(childRow => childRow.parentId === row.id);
    }, [allRows, row.id, hasChildren]);

    const currentIsLast = useMemo(() => {
        const isLastInSiblings = rowIndex === siblings.length - 1;

        return [...isLast, isLastInSiblings];
    }, [rowIndex, siblings.length, isLast]);

    return (
        <Fragment>
            <ExpandableRow
                hasChildren={hasChildren}
                indent={indent}
                index={rowIndex}
                isLast={isLast}
                isOpen={isOpen}
                lastActivity={row.lastActivity}
                lastEdited={row.lastEdited}
                name={row.name}
                onClick={handleClick}
                parentId={parentId}
                rowId={row.id}
                rows={siblings}
                style={style}
            />

            {isOpen &&
                hasChildren &&
                children.map((childRow, childIndex) => (
                    <DataGridRow
                        allRows={allRows}
                        indent={indent + 1}
                        isLast={currentIsLast}
                        key={childRow.id}
                        onToggleOpen={onToggleOpen}
                        openItems={openItems}
                        parentId={childRow.parentId}
                        row={childRow}
                        rowIndex={childIndex}
                        style={style}
                    />
                ))}
        </Fragment>
    );
};

export default DataGridRow;
